import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import SharedButton from '@/global/Button/Button';
import type { ProductDemoFeature, ProductDemoFeatureId } from './productDemoFeatures';
import { PRODUCT_DEMO_FEATURES } from './productDemoFeatures';
import type { SearchTourStep } from './searchTourSteps';
import { SEARCH_TOUR_STEPS } from './searchTourSteps';
import { AIDA_TOUR_STEPS } from './aidaTourSteps';
import { ADVANCED_SEARCH_TOUR_STEPS } from './advancedSearchTourSteps';
import { ENRICHMENT_TOUR_STEPS } from './enrichmentTourSteps';
import { API_TOUR_STEPS } from './apiTourSteps';
import { STICKY_NOTES_TOUR_STEPS } from './stickyNotesTourSteps';
import {
  DEMO_FEATURE_PARAM,
  DEMO_STEP_PARAM,
  DEMO_TOUR_PARAM,
  buildTourUrl,
  computeCornerTipPlacement,
  computeTipPlacement,
  getTourFeatureId,
  prepareSearchStep,
  queryTourEl,
  type SpotlightRect,
  type TipPlacement,
} from './productDemoTourHelpers';
import { closeCreatedSearchDemoTab, isSearchWorkspacePath } from '@/hooks/search/useSearchTabNavigation';
import TourStepAsk from './TourStepAsk';
import styles from './ProductDemoTourOverlay.module.css';

type TourGuide = {
  title: string;
  subtitle: string;
  bullets: string[];
};

const TOUR_GUIDES: Record<ProductDemoFeatureId, TourGuide> = {
  search: {
    title: 'Search training',
    subtitle: 'Walk through the table capabilities step by step.',
    bullets: [],
  },
  aida: {
    title: 'AIDA training',
    subtitle: 'Explore conversation + evidence-grounded answers.',
    bullets: [
      'Use the sidebar Recent list to switch conversation threads.',
      'Ask about associations, evidence, or mechanisms.',
      'Follow structured answer sections and jump back into the data.',
    ],
  },
  'advanced-search': {
    title: 'Advanced Search training',
    subtitle: 'Resolve identifiers, review matches, then search or enrich.',
    bullets: [
      'Switch the entity tabs to resolve different identifier types.',
      'Review matches (including close/fuzzy ones) and uncheck what you don’t want.',
      'Run Search selected items (or Enrichment when applicable).',
    ],
  },
  enrichment: {
    title: 'Enrichment training',
    subtitle: 'Paste a set, pick ORA / DSEA, then explore results tabs.',
    bullets: [
      'Choose GENES or VARIANTS input and select the analysis method (ORA/DSEA).',
      'Run Enrichment and inspect ranked diseases.',
      'Switch results tabs to understand method-specific ranking.',
    ],
  },
  api: {
    title: 'REST API training',
    subtitle: 'Browse endpoints, authorize, then inspect JSON responses.',
    bullets: [
      'Browse endpoints in the left panel by resource group.',
      'Authorize with your API key and load example parameters.',
      'Click Try it out and read the JSON response body/metadata.',
    ],
  },
  'sticky-notes': {
    title: 'Sticky Notes training',
    subtitle: 'Capture insights while you work in the app.',
    bullets: [],
  },
  'geo-browse': {
    title: 'ExpressNET training',
    subtitle: 'Gene expression across diseases.',
    bullets: [
      'Filter diseases, genes, and studies with padj / log2FC thresholds.',
      'Open a study for volcano, PCA, top DEGs, and enrichment.',
      'Compare recurrent genes across studies in Gene Details.',
    ],
  },
};

function FeatureBulletsTour({
  guide,
  activeIndex,
  prevFeature,
  nextFeature,
  onExit,
  onPrevFeature,
  onNextFeature,
}: {
  guide: TourGuide;
  activeIndex: number;
  prevFeature: ProductDemoFeature | null;
  nextFeature: ProductDemoFeature | null;
  onExit: () => void;
  onPrevFeature: () => void;
  onNextFeature: () => void;
}) {
  return (
    <div className={styles.overlayRoot} role="dialog" aria-label="Product demo training overlay">
      <div className={styles.overlayHeader}>
        <div className={styles.overlayTitleBlock}>
          <p className={styles.overlayKicker}>Training mode</p>
          <h2 className={styles.overlayTitle}>{guide.title}</h2>
          <p className={styles.overlaySubtitle}>
            {guide.subtitle} · {activeIndex + 1} / {PRODUCT_DEMO_FEATURES.length}
          </p>
        </div>
        <button type="button" className={styles.overlayCloseBtn} aria-label="Exit training" onClick={onExit}>
          <XMarkIcon width={18} height={18} />
        </button>
      </div>

      <div className={styles.overlayBody}>
        <ul className={styles.overlayList}>
          {guide.bullets.map(bullet => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>

        <div className={styles.overlayActions}>
          <div className={styles.overlayNavBtns}>
            <SharedButton
              type="button"
              size="sm"
              variant="terciary"
              disabled={!prevFeature}
              onClick={onPrevFeature}
              arrow="left"
            >
              Previous
            </SharedButton>
            <SharedButton
              type="button"
              size="sm"
              variant="primary"
              disabled={!nextFeature}
              onClick={onNextFeature}
              arrow="right"
            >
              Next
            </SharedButton>
          </div>
          <div className={styles.overlayProgress}>Follow the UI and try actions directly.</div>
        </div>
      </div>
    </div>
  );
}

function SearchSpotlightTour({
  step,
  stepIndex,
  totalSteps,
  targetMissing,
  spotlight,
  tipPlacement,
  tipReady,
  prevFeature,
  nextFeature,
  tourLabel,
  featureId,
  onExit,
  onPrev,
  onNext,
  onJumpToNextFeature,
  onTipSize,
}: {
  step: SearchTourStep;
  stepIndex: number;
  totalSteps: number;
  targetMissing: boolean;
  spotlight: SpotlightRect | null;
  tipPlacement: TipPlacement;
  tipReady: boolean;
  prevFeature: ProductDemoFeature | null;
  nextFeature: ProductDemoFeature | null;
  tourLabel: string;
  featureId: ProductDemoFeatureId;
  onExit: () => void;
  onPrev: () => void;
  onNext: () => void;
  onJumpToNextFeature: () => void;
  onTipSize?: (height: number) => void;
}) {
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;
  const tipRef = useRef<HTMLDivElement | null>(null);
  const [adjustedTop, setAdjustedTop] = useState<number>(tipPlacement.top);

  useLayoutEffect(() => {
    setAdjustedTop(tipPlacement.top);
  }, [tipPlacement.top, tipPlacement.left, step.id, stepIndex]);

  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    onTipSize?.(rect.height);
    const maxBottom = window.innerHeight - 12;
    if (rect.bottom > maxBottom) {
      const delta = rect.bottom - maxBottom;
      setAdjustedTop(prev => Math.max(12, prev - delta));
    }
  }, [tipPlacement.left, tipPlacement.top, step.id, stepIndex, step.body, onTipSize]);

  return (
    <div className={styles.spotlightRoot} role="dialog" aria-label="Search training step">
      {!spotlight && <div className={styles.spotlightScrim} aria-hidden="true" />}
      {spotlight && (
        <div
          className={styles.spotlightHole}
          style={{
            top: spotlight.top - 6,
            left: spotlight.left - 6,
            width: spotlight.width + 12,
            height: spotlight.height + 12,
          }}
          aria-hidden="true"
        />
      )}

      <div
        ref={tipRef}
        data-demo-tour="spotlight-tip"
        className={`${styles.spotlightTip}${tipReady ? ` ${styles.spotlightTipReady}` : ''}`}
        style={{
          top: adjustedTop,
          left: tipPlacement.left,
          maxWidth: tipPlacement.maxWidth,
          visibility: tipReady ? 'visible' : 'hidden',
        }}
        onMouseDown={event => event.stopPropagation()}
        onPointerDown={event => event.stopPropagation()}
        onClick={event => event.stopPropagation()}
      >
        <div className={styles.overlayHeader}>
          <div className={styles.overlayTitleBlock}>
            <p className={styles.overlayKicker}>
              {tourLabel} · step {stepIndex + 1} / {totalSteps}
            </p>
            <h2 className={styles.overlayTitle}>{step.title}</h2>
          </div>
          <button
            type="button"
            className={styles.overlayCloseBtn}
            aria-label="Exit training"
            onClick={onExit}
          >
            <XMarkIcon width={18} height={18} />
          </button>
        </div>

        <p className={styles.stepBody}>{step.body}</p>
        {(step.note || targetMissing) && (
          <p className={styles.stepNote}>
            {targetMissing
              ? 'This control is not visible right now (sign-in, plan, or UI state). Continue — the next steps may still appear.'
              : step.note}
          </p>
        )}

        <TourStepAsk featureId={featureId} stepId={step.id} stepTitle={step.title} stepBody={step.body} />

        <div className={styles.spotlightActions}>
          <div className={styles.overlayNavBtns}>
            <SharedButton
              type="button"
              size="sm"
              variant="terciary"
              disabled={isFirst && !prevFeature}
              onClick={onPrev}
              arrow="left"
            >
              Previous
            </SharedButton>
            <SharedButton type="button" size="sm" variant="primary" onClick={onNext} arrow="right">
              {isLast ? (nextFeature ? 'Next feature' : 'Finish') : 'Next'}
            </SharedButton>
          </div>
          {nextFeature && (
            <button
              type="button"
              className={styles.spotlightNextFeatureBtn}
              onClick={onJumpToNextFeature}
              aria-label="Next feature"
            >
              Next feature <span aria-hidden="true">→</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ProductDemoTourOverlay() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const isTourActive = searchParams.get(DEMO_TOUR_PARAM) === '1';
  const featureId = getTourFeatureId(searchParams);

  const isAllowedToolPath = [
    '/examples',
    '/search',
    '/Assistant',
    '/advancedSearch',
    '/enrichment',
    '/interactive-console',
    '/Profile-area',
  ].includes(location.pathname);

  const feature = useMemo(
    () => (featureId ? PRODUCT_DEMO_FEATURES.find(f => f.id === featureId) : null),
    [featureId],
  );

  const guide = feature ? TOUR_GUIDES[feature.id] : null;
  const activeIndex = feature ? PRODUCT_DEMO_FEATURES.findIndex(f => f.id === feature.id) : -1;
  const prevFeature = activeIndex > 0 ? PRODUCT_DEMO_FEATURES[activeIndex - 1] : null;
  const nextFeature =
    activeIndex >= 0 && activeIndex < PRODUCT_DEMO_FEATURES.length - 1
      ? PRODUCT_DEMO_FEATURES[activeIndex + 1]
      : null;

  const isSearchTour = feature?.id === 'search';
  const isAidaTour = feature?.id === 'aida';
  const isAdvancedSearchTour = feature?.id === 'advanced-search';
  const isEnrichmentTour = feature?.id === 'enrichment';
  const isApiTour = feature?.id === 'api';
  const isStickyNotesTour = feature?.id === 'sticky-notes';
  const isSpotlightTour =
    isSearchTour || isAidaTour || isAdvancedSearchTour || isEnrichmentTour || isApiTour || isStickyNotesTour;

  const spotlightSteps = isSearchTour
    ? SEARCH_TOUR_STEPS
    : isAidaTour
      ? AIDA_TOUR_STEPS
      : isAdvancedSearchTour
        ? ADVANCED_SEARCH_TOUR_STEPS
        : isEnrichmentTour
          ? ENRICHMENT_TOUR_STEPS
          : isApiTour
            ? API_TOUR_STEPS
            : isStickyNotesTour
              ? STICKY_NOTES_TOUR_STEPS
              : null;

  const stepIndex = useMemo(() => {
    if (!isSpotlightTour || !spotlightSteps) return 0;
    const raw = Number(searchParams.get(DEMO_STEP_PARAM) || '1');
    if (!Number.isFinite(raw) || raw < 1) return 0;
    return Math.min(spotlightSteps.length - 1, Math.max(0, Math.floor(raw) - 1));
  }, [isSpotlightTour, spotlightSteps, searchParams]);

  const step = spotlightSteps ? spotlightSteps[stepIndex] : null;

  const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null);
  const [targetMissing, setTargetMissing] = useState(false);
  const [tipPlacement, setTipPlacement] = useState<TipPlacement>(() => computeTipPlacement(null));
  const [tipReady, setTipReady] = useState(false);
  const tipHeightRef = useRef(200);
  const lastStepIdRef = useRef<string | null>(null);
  const preparedStepIdRef = useRef<string | null>(null);

  const handleTipSize = useCallback((height: number) => {
    if (height > 40) tipHeightRef.current = Math.min(height, 280);
  }, []);

  const measureTarget = useCallback((currentStep: SearchTourStep) => {
    const candidates = [
      currentStep.selector,
      currentStep.fallbackSelector,
      // Last-resort anchors so the tip never floats with a full blank scrim.
      currentStep.id === 'my-score' ? '[data-demo-tour="sidebar-my-score-toggle"]' : null,
      currentStep.id === 'my-score' ? '[data-demo-tour="sidebar-my-score"]' : null,
      currentStep.id === 'my-score' ? '[data-demo-tour="table-controls"][data-demo-open="true"]' : null,
    ].filter(Boolean) as string[];

    let el: HTMLElement | null = null;
    for (const selector of candidates) {
      const found = queryTourEl(selector);
      if (!found) continue;
      const rect = found.getBoundingClientRect();
      if (rect.width >= 2 && rect.height >= 2) {
        el = found;
        break;
      }
    }

    const primaryMissing = !queryTourEl(currentStep.selector);
    setTargetMissing(primaryMissing);

    const useCorner = currentStep.tipPlacement === 'corner' || !el;

    if (!el) {
      setSpotlight(null);
      setTipPlacement(computeCornerTipPlacement(tipHeightRef.current));
      setTipReady(true);
      return;
    }

    const rect = el.getBoundingClientRect();
    const next: SpotlightRect = {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    };
    setSpotlight(next);
    setTipPlacement(
      useCorner
        ? computeCornerTipPlacement(tipHeightRef.current)
        : computeTipPlacement(next, tipHeightRef.current),
    );
    setTipReady(true);

    const fullyVisible =
      rect.top >= 0 && rect.bottom <= window.innerHeight && rect.left >= 0 && rect.right <= window.innerWidth;
    if (!fullyVisible) {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    }
  }, []);

  useEffect(() => {
    if (!isTourActive || !isSpotlightTour) {
      document.body.classList.remove('demo-tour-active');
      return;
    }
    document.body.classList.add('demo-tour-active');
    return () => document.body.classList.remove('demo-tour-active');
  }, [isTourActive, isSpotlightTour]);

  useLayoutEffect(() => {
    if (!isTourActive || !isSpotlightTour || !step) return;

    const stepChanged = lastStepIdRef.current !== step.id;
    if (stepChanged) {
      lastStepIdRef.current = step.id;
      setTipReady(false);
      preparedStepIdRef.current = null;
    }

    // Run prepare once per step (repeating clicks can toggle menus/sidebar closed).
    if (preparedStepIdRef.current !== step.id) {
      preparedStepIdRef.current = step.id;
      prepareSearchStep(step.prepare);
    }

    measureTarget(step);

    // Re-measure while async UI settles.
    // Sidebar open → section expand needs a few prepare passes across React renders.
    const timers = [60, 180, 450, 900, 1600, 2800].map(ms =>
      window.setTimeout(() => {
        if (ms === 180 || ms === 450 || ms === 900) {
          prepareSearchStep(step.prepare);
        }
        measureTarget(step);
      }, ms),
    );

    // Never leave the tip invisible if measurement is slow.
    const tipSafety = window.setTimeout(() => setTipReady(true), 700);

    const onResize = () => measureTarget(step);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);

    return () => {
      timers.forEach(id => window.clearTimeout(id));
      window.clearTimeout(tipSafety);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [isTourActive, isSpotlightTour, step, measureTarget, location.pathname, location.search]);

  const goToSpotlightStep = useCallback(
    (nextStepIndex: number) => {
      if (!feature) return;
      // Only update demoStep on the current URL — rebuilding from getDefaultSearchUrl()
      // remounts Search and looks like a table reload on every Next.
      const params = new URLSearchParams(location.search);
      params.set(DEMO_TOUR_PARAM, '1');
      params.set(DEMO_FEATURE_PARAM, feature.id);
      params.set(DEMO_STEP_PARAM, String(nextStepIndex + 1));
      const hash = location.hash || '';
      navigate(`${location.pathname}?${params.toString()}${hash}`, { replace: true });
    },
    [feature, navigate, location.pathname, location.search, location.hash],
  );

  const goToTourUrl = useCallback(
    (url: string) => {
      const destinationPath = url.split('?')[0];
      if (isSearchWorkspacePath(location.pathname) && !isSearchWorkspacePath(destinationPath)) {
        closeCreatedSearchDemoTab();
      }
      navigate(url);
    },
    [location.pathname, navigate],
  );

  const exitTour = useCallback(() => {
    if (isSearchWorkspacePath(location.pathname)) {
      closeCreatedSearchDemoTab();
    }
    navigate('/product-demo');
  }, [location.pathname, navigate]);

  if (!isTourActive || !feature || !guide || !isAllowedToolPath) return null;

  if (isSpotlightTour && step) {
    const handlePrev = () => {
      if (stepIndex > 0) {
        goToSpotlightStep(stepIndex - 1);
        return;
      }
      if (prevFeature) goToTourUrl(buildTourUrl(prevFeature));
    };

    const handleNext = () => {
      if (spotlightSteps && stepIndex < spotlightSteps.length - 1) {
        goToSpotlightStep(stepIndex + 1);
        return;
      }
      if (nextFeature) {
        goToTourUrl(buildTourUrl(nextFeature));
        return;
      }
      exitTour();
    };

    const handleJumpToNextFeature = () => {
      if (nextFeature) goToTourUrl(buildTourUrl(nextFeature));
    };

    return (
      <SearchSpotlightTour
        step={step}
        stepIndex={stepIndex}
        totalSteps={spotlightSteps?.length ?? SEARCH_TOUR_STEPS.length}
        targetMissing={targetMissing}
        spotlight={spotlight}
        tipPlacement={tipPlacement}
        tipReady={tipReady}
        prevFeature={prevFeature}
        nextFeature={nextFeature}
        tourLabel={feature.label}
        featureId={feature.id}
        onExit={exitTour}
        onPrev={handlePrev}
        onNext={handleNext}
        onJumpToNextFeature={handleJumpToNextFeature}
        onTipSize={handleTipSize}
      />
    );
  }

  return (
    <FeatureBulletsTour
      guide={guide}
      activeIndex={activeIndex}
      prevFeature={prevFeature}
      nextFeature={nextFeature}
      onExit={exitTour}
      onPrevFeature={() => prevFeature && goToTourUrl(buildTourUrl(prevFeature))}
      onNextFeature={() => nextFeature && goToTourUrl(buildTourUrl(nextFeature))}
    />
  );
}
